<script setup lang="ts">
import ListGroup from '@/components/ui/ListGroup.vue';
import LottieAnimation from '@/components/ui/LottieAnimation.vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import SkeletonBlock from '@/components/ui/SkeletonBlock.vue';

import RouteItem from './RouteItem.vue';

import Common from '@/libs/common';
import { compareRoutes } from '@pysae/transit-core/helpers';
import { computed, ref } from 'vue';
import emptyBox from '@/assets/img/emptyBox.json';
import type { Route } from '@/@types/gtfs-ressource';
import type { DeviceCount } from '@/@types/device';
import type { OnDemandRoute } from '@/@types/on-demand-route';
import { onDemandServiceState, type OnDemandServiceState } from '@/libs/on-demand-helper';
import { useAppStore } from '@/stores/app';
import { useDevicesCountPolling } from '@/use/devices-count-polling';
import { useListLoadMore } from '@/use/list-load-more';
import { useDevicesStore } from '@/stores/devices';
import { useGtfsStore } from '@/stores/gtfs';
import { useOnDemandStore } from '@/stores/on-demand';

type RouteRow =
  | { key: string; route: Route; onDemand?: undefined }
  | { key: string; route: OnDemandRoute; onDemand: { state: OnDemandServiceState; vehicleCount: number } };

const appStore = useAppStore();
const devicesStore = useDevicesStore();
const gtfsStore = useGtfsStore();
const onDemandStore = useOnDemandStore();

const search = ref<string>('');
const listEl = ref<HTMLElement>();
const scrollLoadingIncrement = ref<number>(20);

const onlineDevicesCount = computed<Array<DeviceCount>>(() => devicesStore.onlineDevicesCount);

/** Route ids running at least one trip today, to tell "no vehicle" from "no service". */
const routeIdsWithServiceToday = computed<Set<string>>(
  () => new Set(Object.values(gtfsStore.todayTrips()).map(trip => trip.route_id)),
);

// The on-demand routes mix with the GTFS ones; their own key keeps the two id spaces apart.
const gtfsKey = (routeId: string) => `gtfs:${routeId}`;
const onDemandKey = (routeId: string) => `on-demand:${routeId}`;

const allRows = computed<Array<RouteRow>>(() => [
  ...Object.values(gtfsStore.gtfsData.routes).map(route => ({ key: gtfsKey(route.route_id), route })),
  ...onDemandStore.routes.map(route => {
    const vehicleCount = devicesStore.onDemandVehicleCounts.get(route.route_id) ?? 0;
    return {
      key: onDemandKey(route.route_id),
      route,
      onDemand: { state: onDemandServiceState(route, appStore.todayDateGtfs, vehicleCount), vehicleCount },
    };
  }),
]);

// Normalized once per network, not on every keystroke nor on every count poll.
const searchableNames = computed<Map<string, Array<string>>>(() => {
  const names = (route: Route | OnDemandRoute) => [
    Common.normalize(route.route_long_name ?? ''),
    Common.normalize(route.route_short_name),
  ];
  return new Map([
    ...Object.values(gtfsStore.gtfsData.routes).map(
      route => [gtfsKey(route.route_id), names(route)] as const,
    ),
    ...onDemandStore.routes.map(route => [onDemandKey(route.route_id), names(route)] as const),
  ]);
});

// Kept apart from the search: typing only filters this list, it does not sort it again.
const rankedRoutes = computed<Array<RouteRow>>(() => {
  const ranks = new Map(allRows.value.map(row => [row.key, rank(row)]));
  // Inside a rank, the network's own order: route_sort_order first, alphanumeric fallback.
  return [...allRows.value].sort(
    (a, b) => (ranks.get(a.key) ?? 0) - (ranks.get(b.key) ?? 0) || compareRoutes(a.route, b.route),
  );
});

const routes = computed<Array<RouteRow>>(() => {
  if (!search.value) return rankedRoutes.value;
  const searchedText = Common.normalize(search.value);
  return rankedRoutes.value.filter(row =>
    searchableNames.value.get(row.key)?.some(name => name.includes(searchedText)),
  );
});

/** Nothing to list yet: the network and its on-demand routes are still loading. */
const isLoading = computed<boolean>(
  () => allRows.value.length === 0 && (appStore.isLoadingAsBoolean || onDemandStore.loading),
);

const onlineDeviceCountByRouteId = computed<Map<string, DeviceCount>>(
  () => new Map(onlineDevicesCount.value.map(counter => [counter.route_id, counter])),
);

const visibleRoutes = computed<Array<RouteRow>>(() => routes.value.slice(0, scrollLoadingIncrement.value));

useDevicesCountPolling();

/**
 * Favorites first, then the lines a passenger can actually catch right now:
 * connected vehicles, then running today without one, then out of service.
 */
function rank({ route, onDemand }: RouteRow): number {
  if (onDemand) {
    if (appStore.favorites.onDemandRoutes?.[route.route_id]) return 0;
    if (onDemand.state === 'live') return 1;
    if (onDemand.state === 'noVehicle') return 2;
    return 3;
  }
  if (appStore.favorites.routes?.[route.route_id]) return 0;
  if (onlineDeviceCountByRouteId.value.has(route.route_id)) return 1;
  if (routeIdsWithServiceToday.value.has(route.route_id)) return 2;
  return 3;
}

function getOnlineDeviceCountByRouteId(routeId: string): DeviceCount | undefined {
  return onlineDeviceCountByRouteId.value.get(routeId);
}

/**
 * Load data step by step to improve Vue performance. The bound is read from
 * the current list: a latched flag set by a short search result would cap the
 * full list once the search is cleared.
 */
function loadMore() {
  if (scrollLoadingIncrement.value < routes.value.length) scrollLoadingIncrement.value += 25;
}

useListLoadMore(listEl, loadMore, () => scrollLoadingIncrement.value < routes.value.length);
</script>

<template>
  <div class="routes-page">
    <div class="routes-page__search">
      <SearchBar v-model="search" :placeholder="$t('schedules.searchRoute')" />
    </div>

    <div ref="listEl" class="routes-page__list">
      <ListGroup>
        <RouteItem
          v-for="row in visibleRoutes"
          :key="row.key"
          :device-count="row.onDemand ? undefined : getOnlineDeviceCountByRouteId(row.route.route_id)"
          :on-demand="row.onDemand"
          :route="row.route"
        />
      </ListGroup>
      <div v-if="isLoading" class="routes-page__skeleton" aria-busy="true">
        <div v-for="index in 4" :key="index" class="routes-page__skeleton-row">
          <div class="routes-page__skeleton-details">
            <SkeletonBlock class="routes-page__skeleton-badge" />
            <SkeletonBlock class="routes-page__skeleton-name" />
            <SkeletonBlock class="routes-page__skeleton-status" />
          </div>
          <SkeletonBlock shape="circle" class="routes-page__skeleton-bookmark" />
        </div>
      </div>
      <div v-if="routes.length === 0 && search" class="routes-page__empty">
        {{ $t('search.noResults') }}
        <LottieAnimation :animation-data="emptyBox" class="routes-page__empty-animation" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.routes-page {
  background-color: $color-white;
  padding-bottom: $spacing-5;

  // Pinned to the top of the scrolling viewport: the list slides under it,
  // hence the full-width band and its own background.
  &__search {
    background-color: $color-white;
    padding: $spacing-4 $spacing-5 $spacing-2;
    position: sticky;
    top: 0;
    z-index: 1;

    .searchbar {
      margin-bottom: 0;
    }

    // Lets the rows fade out under the bar instead of being cut by its edge.
    &::after {
      background: linear-gradient($color-white, rgba($color-white, 0));
      content: '';
      height: $spacing-4;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      top: 100%;
    }
  }

  .list-group {
    margin-top: $spacing-4;
  }

  &__skeleton-row {
    align-items: center;
    display: flex;
    gap: $spacing-4;
    padding: $spacing-3 $spacing-5;
  }

  &__skeleton-details {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-1;
  }

  &__skeleton-badge {
    width: 48px;
  }

  &__skeleton-name {
    width: 75%;
  }

  &__skeleton-status {
    width: 50%;
  }

  &__skeleton-bookmark {
    width: 40px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-4;
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    padding: $spacing-2 $spacing-5;
    text-align: center;
  }

  &__empty-animation {
    width: min(160px, 50vw);
    aspect-ratio: 1;
  }
}
</style>
